import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Input from '../Input';
import Label from '../Label';
import CloseIcon from '../CloseIcon';
import { PrimaryButton, SecondaryButton } from '../ButtonsV2';

/**
 * FilterModal Component
 * This component renders a modal for filtering events based on a list of provided filters.
 * It includes fields for entering a filter value, buttons for applying or clearing filters,
 * and supports closing the modal by clicking outside or pressing the "Escape" key.
 * @param {initialFilterValues} Object The initial filter values passed down to the modal. It contains the default filter state.
 * @param {onClear} function A function that will be called when the "Clear" button is clicked. It should reset the filters.
 * @param {onApply} function A function that will be called when the "Apply" button is clicked, passing the current filter values.
 * @param {onClose} function A function that will be called when the modal is closed, either by clicking outside or pressing "Escape" or the closed button.
 * @param {isFilterLoading} boolean A boolean that determines if the filters are currently being loaded (used to disable the "Apply" button).
 * @returns
 */
const FilterModal = ({
  initialFilterValues,
  onClear,
  onApply,
  onClose,
  isFilterLoading = false,
}) => {
  const modalRef = useRef(null);
  const [filters, setFilters] = useState(initialFilterValues);

  useEffect(() => {
    setFilters(initialFilterValues);
  }, [initialFilterValues]);

  useEffect(() => {
    const handleEsc = (e) => e.key === 'Escape' && onClose();

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleFilterChange = (key, value) => {
    setFilters((prevFilters) => ({ ...prevFilters, [key]: value }));
  };

  const handleClear = () => {
    onClear();
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const onEventIdNumber = (e) => {
    const value = e.target.value.trim();

    if (!value || !isNaN(value)) {
      handleFilterChange('event_id', value);
    }
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  const disabledButtons = !filters.event_id || isFilterLoading;

  return (
    <div
      ref={modalRef}
      className="absolute top-[55px] right-[0px] flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg p-4 w-80 relative z-10"
        onClick={handleModalClick}
      >
        <div className="flex justify-end">
          <button
            className="p-[12px] text-gray-500 bg-[#F2F4F8] rounded-full"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <CloseIcon size="11" />
          </button>
        </div>

        <div className="flex flex-col items-start">
          <Label variant="dark">Search by Event Number</Label>
          <Input
            variant="tertiary"
            placeholder="Enter event number"
            value={filters.event_id}
            onChange={onEventIdNumber}
            customClass="mt-4 mb-5"
          />
        </div>

        <div className="flex justify-between mt-4 gap-3">
          <SecondaryButton fullWidth onClick={handleClear} disabled={disabledButtons}>
            Clear
          </SecondaryButton>
          <PrimaryButton onClick={handleApply} fullWidth disabled={disabledButtons}>
            Apply
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

FilterModal.propTypes = {
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  initialFilterValues: PropTypes.object.isRequired,
  isFilterLoading: PropTypes.bool.isRequired,
};

export default FilterModal;
